import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { checkPaymentStatus } from '../config/payment';
import { getBookingById } from '../services/dataService';
import { sendBookingEmails } from '../services/emailService';
import '../styles/PaymentResult.css';

// ============================================
// Page de succès du paiement Stripe (version robuste)
// ============================================
// Stratégie d'affichage en 3 niveaux :
//   1. IMMÉDIATEMENT : on affiche un message de succès basique
//      basé uniquement sur l'URL (pas d'attente côté serveur)
//   2. ASYNC : on tente de récupérer la résa pour enrichir l'affichage
//      (détails acompte/solde). Si ça échoue, on garde le niveau 1.
//   3. EMAIL : on envoie l'email de confirmation (en arrière-plan)
//   4. ANALYTICS : on envoie l'event `purchase` à GA4/Google Ads
//      (en utilisant la valeur totale même en mode acompte)
//
// Cette stratégie garantit qu'AUCUNE erreur backend ne provoque
// une page blanche ou une attente infinie pour le client.
// ============================================

// ============================================
// Helper : envoie l'event purchase au dataLayer GTM
// Ne dépend pas de window pour fonctionner aussi en SSR/test
// Envoie UNE SEULE fois par bookingId (évite les doublons)
// ============================================
const pushPurchaseEvent = (bookingData, bookingId) => {
  if (typeof window === 'undefined') return;
  if (!bookingData || !bookingId) return;

  // Détermine si on est en mode acompte (pour l'analyse)
  const isDepositMode = bookingData.paymentMethod === 'stripe_deposit' || bookingData.depositRequired;

  // La valeur de conversion = prix TOTAL de la résa
  // (même en mode acompte, on compte la valeur business totale)
  const value = Number(bookingData.totalPrice) || 0;

  // Initialise le dataLayer si pas déjà fait (GTM le fait normalement)
  window.dataLayer = window.dataLayer || [];

  // Reset ecommerce avant le push (best practice GA4)
  window.dataLayer.push({ ecommerce: null });

  // Push de l'event purchase au format GA4 Enhanced Ecommerce
  window.dataLayer.push({
    event: 'purchase',
    ecommerce: {
      transaction_id: bookingId,
      value: value,
      currency: 'EUR',
      payment_type: bookingData.paymentMethod || 'stripe',
      // Métadonnées supplémentaires utiles pour l'analyse
      deposit_mode: isDepositMode,
      deposit_amount: isDepositMode ? Number(bookingData.depositAmount) || 0 : 0,
      remaining_amount: isDepositMode ? Number(bookingData.remainingAmount) || 0 : 0,
      items: [
        {
          item_id: bookingData.formulaId || bookingData.formula || 'lasertag-session',
          item_name: bookingData.formulaName || bookingData.formula || 'Réservation LaserBay',
          item_category: 'Laser Tag',
          price: value,
          quantity: Number(bookingData.numberOfPlayers) || Number(bookingData.playersCount) || 1
        }
      ]
    }
  });

  console.log('[Analytics] Event purchase envoyé à GA4', {
    transaction_id: bookingId,
    value: value,
    deposit_mode: isDepositMode
  });
};

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // États
  const [booking, setBooking] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [enrichmentDone, setEnrichmentDone] = useState(false);
  
  // useRef pour ne lancer le process qu'UNE fois (StrictMode de React)
  const processedRef = useRef(false);
  // useRef pour s'assurer que l'event GA4 n'est envoyé qu'UNE fois
  const purchaseEventSentRef = useRef(false);
  
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    
    const enrichAndNotify = async () => {
      console.log('[PaymentSuccess] Démarrage', { sessionId, bookingId });
      
      // 1. Tenter de vérifier le statut Stripe (non bloquant)
      if (sessionId) {
        try {
          const result = await checkPaymentStatus(sessionId);
          console.log('[PaymentSuccess] Statut Stripe:', result?.status);
        } catch (err) {
          console.warn('[PaymentSuccess] Vérification Stripe échouée (non critique):', err.message);
        }
      }
      
      // 2. Récupérer la réservation.
      //    Le webhook Stripe et le retour du client sont concurrents :
      //    on réessaie plusieurs fois pour laisser au webhook le temps
      //    de basculer la résa en "confirmed" et d'écrire les montants
      //    (acompte / solde). Sans ça, on afficherait des données
      //    incomplètes et surtout on raterait l'envoi des emails.
      if (bookingId) {
        try {
          let bookingData = null;
          const MAX_TENTATIVES = 5;

          for (let tentative = 1; tentative <= MAX_TENTATIVES; tentative++) {
            await new Promise(resolve => setTimeout(resolve, tentative === 1 ? 1500 : 1200));

            console.log(`[PaymentSuccess] Lecture du booking (tentative ${tentative}/${MAX_TENTATIVES})...`);
            bookingData = await getBookingById(bookingId);

            if (!bookingData) continue;

            // Le webhook a fait son travail ?
            const webhookOk =
              bookingData.status === 'confirmed' ||
              bookingData.isPaid === true ||
              bookingData.depositPaid === true;

            if (webhookOk) {
              console.log('[PaymentSuccess] Webhook confirmé au bout de', tentative, 'tentative(s)');
              break;
            }

            console.log('[PaymentSuccess] Webhook pas encore passé, nouvelle tentative...');
          }
          
          if (bookingData) {
            console.log('[PaymentSuccess] Booking récupéré:', {
              id: bookingData.id,
              status: bookingData.status,
              isPaid: bookingData.isPaid,
              depositPaid: bookingData.depositPaid,
              paymentMethod: bookingData.paymentMethod
            });
            setBooking(bookingData);

            // ⚡ ANALYTICS : envoi de l'event purchase à GA4 dès qu'on a les données
            if (!purchaseEventSentRef.current) {
              purchaseEventSentRef.current = true;
              pushPurchaseEvent(bookingData, bookingId);
            }
            
            if (bookingData.contactEmail && !emailSent) {
              try {
                console.log('[PaymentSuccess] Envoi email à', bookingData.contactEmail);
                // force: true → on est sur la page de retour Stripe, le paiement
                // est avéré. On envoie même si le webhook n'a pas encore basculé
                // le statut, sinon l'email serait perdu définitivement.
                const emailResult = await sendBookingEmails(
                  { ...bookingData, status: 'confirmed' },
                  { force: true }
                );
                if (emailResult.clientSent) {
                  console.log('[PaymentSuccess] Email client envoyé');
                  setEmailSent(true);
                }
                if (emailResult.adminSent) {
                  console.log('[PaymentSuccess] Notification admin envoyée');
                }
              } catch (emailErr) {
                console.error('[PaymentSuccess] Erreur envoi email:', emailErr);
              }
            }
          } else {
            console.warn('[PaymentSuccess] Booking introuvable pour ID', bookingId);

            // ⚡ FALLBACK ANALYTICS : on envoie quand même un event purchase minimal
            // pour ne pas perdre la conversion si la BDD est lente/down
            if (!purchaseEventSentRef.current) {
              purchaseEventSentRef.current = true;
              if (typeof window !== 'undefined') {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({ ecommerce: null });
                window.dataLayer.push({
                  event: 'purchase',
                  ecommerce: {
                    transaction_id: bookingId,
                    value: 0,
                    currency: 'EUR',
                    payment_type: 'stripe',
                    items: [{
                      item_id: 'lasertag-session',
                      item_name: 'Réservation LaserBay',
                      item_category: 'Laser Tag',
                      price: 0,
                      quantity: 1
                    }]
                  }
                });
                console.log('[Analytics] Event purchase FALLBACK envoyé (booking introuvable)');
              }
            }
          }
        } catch (err) {
          console.error('[PaymentSuccess] Erreur récupération booking:', err);
        }
      }
      
      setEnrichmentDone(true);
    };
    
    // Garde-fou ultime : même si tout plante, on marque comme "fait" après 15s
    // (les 5 tentatives de lecture peuvent prendre jusqu'à ~7s)
    const safetyTimeout = setTimeout(() => {
      console.warn('[PaymentSuccess] Timeout de sécurité atteint');
      setEnrichmentDone(true);
    }, 15000);
    
    enrichAndNotify().finally(() => clearTimeout(safetyTimeout));
    
    return () => clearTimeout(safetyTimeout);
  }, [sessionId, bookingId, emailSent]);

  // Détection du mode acompte
  const isDepositMode = booking?.paymentMethod === 'stripe_deposit' || booking?.depositRequired;
  const depositAmount = booking?.depositAmount;
  const remainingAmount = booking?.remainingAmount;
  const totalPrice = booking?.totalPrice;
  const refDisplay = bookingId ? `LB-${bookingId.substring(0, 8).toUpperCase()}` : null;

  return (
    <div className="payment-result-page">
      <div className="payment-result-card success">
        {/* Le succès s'affiche IMMÉDIATEMENT, on n'attend rien */}
        <div className="result-icon">✅</div>
        
        {booking && isDepositMode ? (
          <>
            <h2>Acompte confirmé !</h2>
            <p>Votre réservation est <strong>validée</strong>.</p>
            
            {depositAmount !== undefined && remainingAmount !== undefined && (
              <div className="payment-summary">
                <div className="payment-summary-line">
                  <span>🔒 Acompte versé en ligne</span>
                  <strong className="paid">{depositAmount}€ ✓</strong>
                </div>
                <div className="payment-summary-line">
                  <span>💶 Solde à régler sur place</span>
                  <strong className="remaining">{remainingAmount}€</strong>
                </div>
                <div className="payment-summary-line total">
                  <span>Total</span>
                  <strong>{totalPrice}€</strong>
                </div>
              </div>
            )}
          </>
        ) : booking ? (
          <>
            <h2>Paiement confirmé !</h2>
            <p>Votre réservation est enregistrée et <strong>intégralement payée</strong>.</p>
            {totalPrice && (
              <div className="payment-summary">
                <div className="payment-summary-line total">
                  <span>Montant payé</span>
                  <strong>{totalPrice}€</strong>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <h2>Paiement confirmé !</h2>
            <p>Merci, votre paiement a bien été reçu.</p>
            {!enrichmentDone && (
              <p className="result-detail" style={{ opacity: 0.7 }}>
                <span className="spinning" style={{ display: 'inline-block' }}>⏳</span>
                {' '}Récupération des détails...
              </p>
            )}
          </>
        )}
        
        <p className="result-detail">
          {emailSent 
            ? '📧 Un email de confirmation vient de vous être envoyé.'
            : booking?.contactEmail 
              ? `📧 Un email de confirmation va vous être envoyé à ${booking.contactEmail}.`
              : '📧 Un email de confirmation va vous être envoyé.'
          }
        </p>
        
        {refDisplay && (
          <p className="result-ref">
            Référence : {refDisplay}
          </p>
        )}
        
        <div className="result-actions">
          {bookingId && (
            <button 
              onClick={() => navigate(`/booking/status/${bookingId}`)} 
              className="result-btn secondary"
            >
              Voir ma réservation
            </button>
          )}
          <button onClick={() => navigate('/')} className="result-btn primary">
            Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Page d'annulation du paiement
// ============================================
function PaymentCancel() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = searchParams.get('booking_id');
  const [booking, setBooking] = useState(null);

  // useRef pour ne lancer l'event analytics qu'une fois
  const cancelEventSentRef = useRef(false);
  
  useEffect(() => {
    if (!bookingId) return;
    getBookingById(bookingId)
      .then(data => {
        if (data) setBooking(data);
      })
      .catch(err => console.error('[PaymentCancel] Erreur récupération booking:', err));
  }, [bookingId]);

  // Envoi de l'event "paiement abandonné" à GA4 (utile pour analyser le funnel)
  useEffect(() => {
    if (cancelEventSentRef.current || !bookingId) return;
    cancelEventSentRef.current = true;

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'payment_cancelled',
        transaction_id: bookingId,
        value: Number(booking?.totalPrice) || 0,
        currency: 'EUR',
        payment_type: booking?.paymentMethod || 'unknown'
      });
      console.log('[Analytics] Event payment_cancelled envoyé');
    }
  }, [bookingId, booking]);
  
  const isDepositMode = booking?.paymentMethod === 'stripe_deposit' || booking?.depositRequired;
  const refDisplay = bookingId ? `LB-${bookingId.substring(0, 8).toUpperCase()}` : null;

  return (
    <div className="payment-result-page">
      <div className="payment-result-card cancel">
        <div className="result-icon">❌</div>
        <h2>Paiement non finalisé</h2>
        
        {isDepositMode ? (
          <>
            <p>L'acompte n'a pas été versé.</p>
            <p className="result-detail">
              ⚠️ <strong>Votre réservation n'est pas confirmée</strong>.
              Pour la valider, merci de réessayer le paiement de l'acompte 
              ou de nous contacter directement.
            </p>
          </>
        ) : (
          <>
            <p>Votre paiement n'a pas été effectué.</p>
            <p className="result-detail">
              ⚠️ <strong>Votre réservation n'est pas confirmée.</strong> Le créneau
              reste bloqué quelques minutes puis sera remis à la disposition
              d'autres clients. Vous pouvez réessayer le paiement ou nous
              contacter directement par téléphone.
            </p>
          </>
        )}
        
        {refDisplay && (
          <p className="result-ref">Référence : {refDisplay}</p>
        )}
        
        <div className="result-actions">
          {bookingId && (
            <button 
              onClick={() => navigate(`/booking/status/${bookingId}`)} 
              className="result-btn secondary"
            >
              Voir ma réservation
            </button>
          )}
          <button onClick={() => navigate('/')} className="result-btn primary">
            Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
}

export { PaymentSuccess, PaymentCancel };
